import { Button, createTheme, ThemeProvider } from '@mui/material';
import cn from 'classnames';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import {
  AuthorBlock,
  AuthorPopup,
  BCBreadcrumbs,
  BCCarousel,
  CircleRating,
  RatingBadge,
  VintageButton,
} from '../../../components';
import { withLayout } from '../../../Layout/Layout';
import { httpGet } from '../../../utils/httpClient';
import stickyRef from '../../../utils/lazyRef';
import styles from './AirlineReview.module.scss';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { CABIN_CLASSES } from '../../../utils/cabinClasses';
import dynamic from 'next/dynamic';
import { InView } from 'react-intersection-observer';
import { replaceHTML } from '../../../components/utils/replaceHTML';
import parse from 'html-react-parser';
import ScrollableContent from '../../../components/ScrollableContent/ScrollableContent';

const SocialMediaLinks = dynamic(() =>
  import('../../../components/SocialMediaLinks/SocialMediaLinks')
);
const CallToSignUp = dynamic(() =>
  import('../../../components/CallToSignUp/CallToSignUp')
);
const FlightSearch = dynamic(() =>
  import('../../../components/FlightSearch/FlightSearch')
);
const RecommendationsList = dynamic(() =>
  import('../../../components/RecommendationsList/RecommendationsList')
);

const theme = createTheme({
  palette: {
    primary: {
      main: '#00454A',
    },
    secondary: {
      main: '#202020',
    },
  },
  typography: {
    fontFamily: '"Libre Franklin", sans-serif',
  },
});
export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};
export const getStaticProps = async ({ params }) => {
  const reviewRes = await httpGet(
    `${process.env.javaApiUrl}/airline/review/${params.id}`
  );
  const recommendationsRes = await httpGet(
    `${process.env.javaApiUrl}/recommendation/airlines/reviews/${params.id}`
  );
  if (reviewRes.status >= 400) {
    return {
      notFound: true,
      revalidate: 60,
    };
  }
  const review = await reviewRes.json();
  if (review.textId !== params.id) {
    return {
      redirect: {
        destination: `/airlines/reviews/${review.textId}`,
        permanent: false,
        // statusCode: 301
      },
    };
  }
  const recommendation = await recommendationsRes.json();

  return {
    props: {
      review,
      recommendation,
    },
    revalidate: 60,
  };
};

function AirlineReview({ review, recommendation, authCompleted }) {
  const userStore = useSelector((state) => state.user.value);
  const executeScroll = (event, ref) => {
    event.preventDefault();
    const target = { ...ref };
    target?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const router = useRouter();
  const [searchToggled, toggleSearch] = useState(true);
  const [seoSchema, setSeoSchema] = useState(null);
  const [mobileSearchToggled, toggleMobileSearch] = useState(false);
  const { elementRef, isSticky } = stickyRef();
  const [includesSponsoredLinks, setIncludesSponsoredLinks] = useState(false);

  useEffect(() => {
    toggleSearch(true);
  }, [isSticky]);
  useEffect(() => {
    if (review.contentMetaData?.seoSchema) {
      setSeoSchema(parse(review?.contentMetaData.seoSchema));
    }

    const includesSponsored = Object.keys(review).some(key => {
      if (typeof review[key] === 'string') {
        return review[key].includes('"noopener noreferrer sponsored"');
      }
    });

    setIncludesSponsoredLinks(includesSponsored);
  }, [review]);
  const airportExperienceRef = React.createRef(),
    seatCabinRef = React.createRef(),
    cuisineRef = React.createRef(),
    beveragesRef = React.createRef(),
    serviceRef = React.createRef(),
    entertainmentRef = React.createRef(),
    amenitiesRef = React.createRef(),
    mediumHaulRef = React.createRef(),
    sustainabilityRef = React.createRef(),
    weLoveRef = React.createRef();
  const options = {
    replace: (domNode) => {
      if (domNode.type === 'tag' && domNode.name === 'a') {
        if (domNode.attribs?.href?.includes('http')) {
          if (domNode.attribs?.target === '_blank') {
            domNode.attribs.target = '_blank';
          }
         
          if (domNode.attribs?.rel?.includes('sponsored')) {
            domNode.attribs.rel = 'noreferrer noopener sponsored';
          } else {
            domNode.attribs.rel = 'noreferrer noopener nofollow';
          }
        }
      }
    },
  };
  const replaceContent = (content) => parse(content, options);
  const renderSearch = (toggler, viewMode, preventCollapsing = false) => {
    return (
      <InView triggerOnce>
        {({ inView, ref }) => (
          <div className="mx-auto" ref={ref}>
            {inView && (
              <FlightSearch
                contentSearch
                toggleSearch={toggler}
                collapsed={!preventCollapsing && isSticky}
                viewMode={viewMode}
                airlineName={review.airline.name}
                airlineCode={review.airline.code}
                airlineClass={
                  CABIN_CLASSES.find(
                    (cabin) => cabin.value === review.cabinClass
                  ).label
                }
                searchSource={'Airline review'}
              />
            )}
          </div>
        )}
      </InView>
    );
  };
  return (
    <>
      <Head>
        <title>
          {review.contentMetaData?.metaTitle ||
            `Review of ${review.airline.name} ${
              CABIN_CLASSES.find((cabin) => cabin.value === review.cabinClass)
                .label
            } - First-Class.com`}
        </title>
        {review.contentMetaData?.seoHtml === 'true' && (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <meta
          name="description"
          content={
            review.contentMetaData?.metaDescription ||
            replaceHTML(review.introduction)
          }
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="first-class.com" />
        <meta
          name="twitter:title"
          content={
            review.contentMetaData?.metaTitle ||
            `Review of ${review.airline.name}
            ${
              CABIN_CLASSES.find((cabin) => cabin.value === review.cabinClass)
                .label
            }
      `
          }
        />
        <link
          rel="canonical"
          href={
            review.contentMetaData?.canonicalTag
              ? process.env.apiUrl + review?.contentMetaData.canonicalTag
              : `${process.env.apiUrl}${router.asPath.split('?')[0]}`
          }
        />
        <meta
          name="twitter:description"
          content={
            review.contentMetaData?.metaDescription ||
            replaceHTML(review.introduction)
          }
        />
        <meta
          name="twitter:image"
          content={
            process.env.mediaUrl +
            review.frontImage?.replace('/', '').replace('view/image/', '')
          }
        />
        <meta
          property="og:title"
          content={
            review.contentMetaData?.metaTitle ||
            `Review of ${review.airline.name}
            ${
              CABIN_CLASSES.find((cabin) => cabin.value === review.cabinClass)
                .label
            }
      `
          }
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:image"
          content={
            process.env.mediaUrl +
            review.frontImage?.replace('/', '').replace('view/image/', '')
          }
        />
        <meta
          property="og:description"
          content={
            review.contentMetaData?.metaDescription ||
            replaceHTML(review.introduction)
          }
        />
        {seoSchema}
      </Head>
      <ThemeProvider theme={theme}>
        <div className="bg-review-bg">
          <div className={cn(styles.imageContainer, 'mx-auto')}>
            <img
              src={
                process.env.mediaUrl +
                review.topImage?.replace('/', '').replace('view/image/', '')
              }
              className={styles.topImage}
              alt={review.topImageAltText}
            />
          </div>
          <div className="container sm:px-5">
            <BCBreadcrumbs headerText={review.airline.name} />
            <div className={cn(styles.headerTop, 'font-fragment')}>
              {review.airline.name}{' '}
              {
                CABIN_CLASSES.find((cabin) => cabin.value === review.cabinClass)
                  .label
              }
            </div>
          </div>
          <div className="col-span-3 flex flex-col items-center sm:hidden">
            {isSticky && (
              <div
                className={cn({
                  [styles.flightSearch]: true,
                  [styles.stickySearch]: isSticky,
                  hidden: !searchToggled,
                })}
                ref={elementRef}
              >
                {renderSearch(toggleSearch, 'sideSearchMinimized')}
              </div>
            )}
          </div>
          <div
            className={cn('col-span-12 flex flex-col items-center lg:hidden')}
          >
            {isSticky && !mobileSearchToggled && (
              <div
                className={cn(styles.flightSearch, {
                  [styles.stickySearch]: isSticky,
                })}
                ref={elementRef}
              >
                <span className={cn(styles.searchHeading, 'font-fragment')}>
                  Search premium flight offers
                </span>
                <Button
                  variant="contained"
                  type="button"
                  onClick={() => toggleMobileSearch(true)}
                >
                  Search
                </Button>
              </div>
            )}
            {isSticky && mobileSearchToggled && (
              <div
                className={cn({
                  [styles.flightSearch]: true,
                  [styles.stickySearch]: isSticky,
                  hidden: !searchToggled && !mobileSearchToggled,
                })}
                ref={elementRef}
              >
                {renderSearch(toggleMobileSearch, 'sideSearchMinimized')}
              </div>
            )}
          </div>
          <div className="container font-libre grid grid-cols-12 sm:px-5">
            <div className="col-span-9 sm:col-span-12 ">
              <div className="w-full grid grid-cols-12 gap-x-2">
                <div className="flex lg:flex-col flex-wrap lg:flex-nowrap col-span-4 sm:col-span-12">
                  <div className="flex sm:flex-col lg:w-full sm:w-1/3">
                    <div className={styles.ratingBadge}>
                      <a className="full-review font-fragment">
                        <RatingBadge version={'badge-tested'} />
                        <span>{review.rating}</span>
                      </a>
                    </div>
                  </div>
                  <div className="flex flex-col sm:w-2/3">
                    <div className={styles.ratingFrac}>
                      <div className={styles.ratingValue}>{review.rating}</div>
                      <div className={styles.ratingMax}>/10</div>
                    </div>
                    <div className={cn(styles.textRating, 'font-libre')}>
                      {review.rating <= 10 &&
                        review.rating > 9 &&
                        'A must-do experience on any travel lovers bucket list'}
                      {review.rating <= 9 &&
                        review.rating > 8 &&
                        'A great flight you will recommend to friends'}
                      {review.rating <= 8 &&
                        review.rating > 7 &&
                        'A good flight you will enjoy and do again'}
                      {review.rating <= 7 &&
                        review.rating > 6 &&
                        'A comfortable flight'}
                      {review.rating < 6 && 'An acceptable flight'}
                    </div>
                  </div>
                  <div className="bc-review w-full">
                    {!!review.airportExperienceRating && (
                      <CircleRating
                        rating={review.airportExperienceRating}
                        text={'Airport Experience'}
                      />
                    )}
                    {!!review.seatCabinRating && (
                      <CircleRating
                        rating={review.seatCabinRating}
                        text={'Cabin & Seat'}
                      />
                    )}
                    {!!review.cuisineRating && (
                      <CircleRating
                        rating={review.cuisineRating}
                        text={'Cuisine'}
                      />
                    )}
                    {!!review.beveragesRating && (
                      <CircleRating
                        rating={review.beveragesRating}
                        text={'Beverages'}
                      />
                    )}
                    {!!review.serviceRating && (
                      <CircleRating
                        rating={review.serviceRating}
                        text={'Service'}
                      />
                    )}
                    {!!review.entertainmentRating && (
                      <CircleRating
                        rating={review.entertainmentRating}
                        text={'Entertainment'}
                      />
                    )}
                    {!!review.amenititesRating && (
                      <CircleRating
                        rating={review.amenititesRating}
                        text={'Amenities & Facilities'}
                      />
                    )}
                    {!!review.mediumHaulRating && (
                      <CircleRating
                        rating={review.mediumHaulRating}
                        text={'Short & Medium Haul'}
                      />
                    )}
                  </div>
                </div>
                <div
                  className={cn(
                    styles.sectionDescription,
                    'col-span-8 sm:col-span-12 sm:mt-5'
                  )}
                >
                  <h1 className={cn(styles.h1, 'font-fragment')}>
                    Review of {review.airline.name}{' '}
                    {
                      CABIN_CLASSES.find(
                        (cabin) => cabin.value === review.cabinClass
                      ).label
                    }
                  </h1>
                  <AuthorPopup
                    userBio={review.userBio}
                    modifiedDate={review.modifiedDate}
                  />
                  <div className={cn('content', styles.article, 'font-libre')}>
                    {replaceContent(review.introduction)}
                  </div>
                </div>
              </div>
            </div>

            <div
              className={cn(
                styles.fightSearchContainer,
                'col-span-3 sm:col-span-12 flex flex-col items-center'
              )}
            >
              {!isSticky && (
                <div
                  className={cn({
                    [styles.flightSearch]: true,
                    [styles.stickySearch]: isSticky,
                    hidden: !searchToggled && !mobileSearchToggled,
                  })}
                  ref={elementRef}
                >
                  {renderSearch(toggleSearch, 'sideSearchMinimized')}
                </div>
              )}
            </div>
            <div
              className={cn(
                styles.headersIndex,
                'col-span-12 w-full font-fragment lg:grid lg:grid-cols-4 sm:grid sm:grid-cols-1 mt-4'
              )}
            >
              <a
                onClick={(event) => executeScroll(event, airportExperienceRef)}
              >
                Airport experience
              </a>
              <a onClick={(event) => executeScroll(event, seatCabinRef)}>
                Cabin &amp; Seat
              </a>
              <a onClick={(event) => executeScroll(event, cuisineRef)}>
                Cuisine
              </a>
              <a onClick={(event) => executeScroll(event, beveragesRef)}>
                Beverages
              </a>
              <a onClick={(event) => executeScroll(event, serviceRef)}>
                Service
              </a>
              <a onClick={(event) => executeScroll(event, entertainmentRef)}>
                Entertainment
              </a>
              <a onClick={(event) => executeScroll(event, amenitiesRef)}>
                Amenities &amp; Facilities
              </a>
              <a onClick={(event) => executeScroll(event, mediumHaulRef)}>
                Short &amp; Medium Haul
              </a>
              <a onClick={(event) => executeScroll(event, sustainabilityRef)}>
                Sustainability
              </a>
              <a onClick={(event) => executeScroll(event, weLoveRef)}>
                What We Love
              </a>
            </div>
          </div>
          <div
            ref={airportExperienceRef}
            className={cn(
              'container grid grid-cols-12 my-20 sm:px-5',
              styles.scrollContainer
            )}
          >
            <div className="col-span-9 sm:col-span-12">
              <div className="w-full grid grid-cols-12 gap-x-2">
                <div
                  className={cn(
                    styles.header,
                    'font-fragment col-span-12 lg:col-span-4'
                  )}
                >
                  <h3 className={styles.cap}>Airport experience</h3>
                  {!!review.airportExperienceRating && (
                    <div className={styles.score}>
                      <span className={styles.points}>
                        {review.airportExperienceRating}
                      </span>
                      <span>/10</span>
                    </div>
                  )}
                </div>
                <div
                  className={cn(
                    styles.sectionDescription,
                    'col-span-12 lg:col-span-8 font-libre'
                  )}
                >
                  <div className={cn(styles.article)}>
                    {replaceContent(review.airportExperience)}
                  </div>
                </div>
              </div>
            </div>
            <div
              className={cn(
                styles.carouselWrapper,
                'col-span-12 lg:w-11/12 lg:mx-auto mt-8'
              )}
            >
              {!!review.airportExperienceImage.length && (
                <BCCarousel showThumbs={false}>
                  {review.airportExperienceImage.map((item, index) => (
                    <div className="relative w-full h-full" key={item.id}>
                      <Image
                        src={
                          process.env.mediaUrl +
                          item.image
                            ?.replace('/', '')
                            .replace('view/image/', '')
                        }
                        blurDataURL={
                          index
                            ? `/_next/image?url=${
                                process.env.mediaUrl +
                                item.image
                                  ?.replace('/', '')
                                  .replace('view/image/', '')
                              }&w=16&q=1`
                            : null
                        }
                        alt={`Airline review Airport experience - ${review.airline.name} - ${index}`}
                        layout="fill"
                        objectFit="contain"
                        placeholder={index ? 'blur' : null}
                      />
                    </div>
                  ))}
                </BCCarousel>
              )}
            </div>
          </div>
          <div
            ref={seatCabinRef}
            className={cn(
              'container grid grid-cols-12 my-20 sm:px-5',
              styles.scrollContainer
            )}
          >
            <div className="col-span-9 sm:col-span-12">
              <div className="w-full grid grid-cols-12 gap-x-2">
                <div
                  className={cn(
                    styles.header,
                    'font- col-span-12 lg:col-span-4'
                  )}
                >
                  <h3 className={styles.cap}>Cabin & Seat</h3>
                  {!!review.seatCabinRating && (
                    <div className={styles.score}>
                      <span className={styles.points}>
                        {review.seatCabinRating}
                      </span>
                      <span>/10</span>
                    </div>
                  )}
                </div>
                <div
                  className={cn(
                    styles.sectionDescription,
                    'col-span-12 lg:col-span-8 font-libre'
                  )}
                >
                  <div className={cn(styles.article)}>
                    {replaceContent(review.seatCabin)}
                  </div>
                </div>
              </div>
            </div>
            <div
              className={cn(
                styles.carouselWrapper,
                'col-span-12 lg:w-11/12 lg:mx-auto mt-8'
              )}
            >
              {!!review.seatCabinImage.length && (
                <BCCarousel showThumbs={false}>
                  {review.seatCabinImage.map((item, index) => (
                    <div className="relative w-full h-full" key={item.id}>
                      <Image
                        src={
                          process.env.mediaUrl +
                          item.image
                            ?.replace('/', '')
                            .replace('view/image/', '')
                        }
                        blurDataURL={
                          index
                            ? `/_next/image?url=${
                                process.env.mediaUrl +
                                item.image
                                  ?.replace('/', '')
                                  .replace('view/image/', '')
                              }&w=16&q=1`
                            : null
                        }
                        alt={`Airline review Cabin & Seat - ${review.airline.name} - ${index}`}
                        layout="fill"
                        objectFit="contain"
                        placeholder={index ? 'blur' : null}
                      />
                    </div>
                  ))}
                </BCCarousel>
              )}
            </div>
          </div>
          <div
            ref={cuisineRef}
            className={cn(
              'container grid grid-cols-12 my-20 sm:px-5',
              styles.scrollContainer
            )}
          >
            <div className="col-span-9 sm:col-span-12">
              <div className="w-full grid grid-cols-12 gap-x-2">
                <div
                  className={cn(
                    styles.header,
                    'font- col-span-12 lg:col-span-4'
                  )}
                >
                  <h3 className={styles.cap}>Cuisine</h3>
                  {!!review.cuisineRating && (
                    <div className={styles.score}>
                      <span className={styles.points}>
                        {review.cuisineRating}
                      </span>
                      <span>/10</span>
                    </div>
                  )}
                </div>
                <div
                  className={cn(
                    styles.sectionDescription,
                    'col-span-12 lg:col-span-8 font-libre'
                  )}
                >
                  <div className={cn(styles.article)}>
                    {replaceContent(review.cuisine)}
                  </div>
                </div>
              </div>
            </div>
            <div
              className={cn(
                styles.carouselWrapper,
                'col-span-12 lg:w-11/12 lg:mx-auto mt-8'
              )}
            >
              {!!review.cuisineImage.length && (
                <BCCarousel showThumbs={false}>
                  {review.cuisineImage.map((item, index) => (
                    <div className="relative w-full h-full" key={item.id}>
                      <Image
                        src={
                          process.env.mediaUrl +
                          item.image
                            ?.replace('/', '')
                            .replace('view/image/', '')
                        }
                        blurDataURL={
                          index
                            ? `/_next/image?url=${
                                process.env.mediaUrl +
                                item.image
                                  ?.replace('/', '')
                                  .replace('view/image/', '')
                              }&w=16&q=1`
                            : null
                        }
                        alt={`Airline review Cuisine - ${review.airline.name} - ${index}`}
                        layout="fill"
                        objectFit="contain"
                        placeholder={index ? 'blur' : null}
                      />
                    </div>
                  ))}
                </BCCarousel>
              )}
            </div>
          </div>
          <div
            ref={beveragesRef}
            className={cn(
              'container grid grid-cols-12 my-20 sm:px-5',
              styles.scrollContainer
            )}
          >
            <div className="col-span-9 sm:col-span-12">
              <div className="w-full grid grid-cols-12 gap-x-2">
                <div
                  className={cn(
                    styles.header,
                    'font- col-span-12 lg:col-span-4'
                  )}
                >
                  <h3 className={styles.cap}>Beverages</h3>
                  {!!review.beveragesRating && (
                    <div className={styles.score}>
                      <span className={styles.points}>
                        {review.beveragesRating}
                      </span>
                      <span>/10</span>
                    </div>
                  )}
                </div>
                <div
                  className={cn(
                    styles.sectionDescription,
                    'col-span-12 lg:col-span-8 font-libre'
                  )}
                >
                  <div className={cn(styles.article)}>
                    {replaceContent(review.beverages)}
                  </div>
                </div>
              </div>
            </div>
            <div
              className={cn(
                styles.carouselWrapper,
                'col-span-12 lg:w-11/12 lg:mx-auto mt-8'
              )}
            >
              {!!review.beveragesImage.length && (
                <BCCarousel showThumbs={false}>
                  {review.beveragesImage.map((item, index) => (
                    <div className="relative w-full h-full" key={item.id}>
                      <Image
                        src={
                          process.env.mediaUrl +
                          item.image
                            ?.replace('/', '')
                            .replace('view/image/', '')
                        }
                        blurDataURL={
                          index
                            ? `/_next/image?url=${
                                process.env.mediaUrl +
                                item.image
                                  ?.replace('/', '')
                                  .replace('view/image/', '')
                              }&w=16&q=1`
                            : null
                        }
                        alt={`Airline review Beverages - ${review.airline.name} - ${index}`}
                        layout="fill"
                        objectFit="contain"
                        placeholder={index ? 'blur' : null}
                      />
                    </div>
                  ))}
                </BCCarousel>
              )}
            </div>
          </div>
          <div
            ref={serviceRef}
            className={cn(
              'container grid grid-cols-12 my-20 sm:px-5',
              styles.scrollContainer
            )}
          >
            <div className="col-span-9 sm:col-span-12">
              <div className="w-full grid grid-cols-12 gap-x-2">
                <div
                  className={cn(
                    styles.header,
                    'font- col-span-12 lg:col-span-4'
                  )}
                >
                  <h3 className={styles.cap}>Service</h3>
                  {!!review.serviceRating && (
                    <div className={styles.score}>
                      <span className={styles.points}>
                        {review.serviceRating}
                      </span>
                      <span>/10</span>
                    </div>
                  )}
                </div>
                <div
                  className={cn(
                    styles.sectionDescription,
                    'col-span-12 lg:col-span-8 font-libre'
                  )}
                >
                  <div className={cn(styles.article)}>
                    {replaceContent(review.service)}
                  </div>
                </div>
              </div>
            </div>
            <div
              className={cn(
                styles.carouselWrapper,
                'col-span-12 lg:w-11/12 lg:mx-auto mt-8'
              )}
            >
              {!!review.serviceImage.length && (
                <BCCarousel showThumbs={false}>
                  {review.serviceImage.map((item, index) => (
                    <div className="relative w-full h-full" key={item.id}>
                      <Image
                        src={
                          process.env.mediaUrl +
                          item.image
                            ?.replace('/', '')
                            .replace('view/image/', '')
                        }
                        blurDataURL={
                          index
                            ? `/_next/image?url=${
                                process.env.mediaUrl +
                                item.image
                                  ?.replace('/', '')
                                  .replace('view/image/', '')
                              }&w=16&q=1`
                            : null
                        }
                        alt={`Airline review Service - ${review.airline.name} - ${index}`}
                        layout="fill"
                        objectFit="contain"
                        placeholder={index ? 'blur' : null}
                      />
                    </div>
                  ))}
                </BCCarousel>
              )}
            </div>
          </div>
          <div
            ref={entertainmentRef}
            className={cn(
              'container grid grid-cols-12 my-20 sm:px-5',
              styles.scrollContainer
            )}
          >
            <div className="col-span-9 sm:col-span-12">
              <div className="w-full grid grid-cols-12 gap-x-2">
                <div
                  className={cn(
                    styles.header,
                    'font- col-span-12 lg:col-span-4'
                  )}
                >
                  <h3 className={styles.cap}>Entertainment</h3>
                  {!!review.entertainmentRating && (
                    <div className={styles.score}>
                      <span className={styles.points}>
                        {review.entertainmentRating}
                      </span>
                      <span>/10</span>
                    </div>
                  )}
                </div>
                <div
                  className={cn(
                    styles.sectionDescription,
                    'col-span-12 lg:col-span-8 font-libre'
                  )}
                >
                  <div className={cn(styles.article)}>
                    {replaceContent(review.entertainment)}
                  </div>
                </div>
              </div>
            </div>
            <div
              className={cn(
                styles.carouselWrapper,
                'col-span-12 lg:w-11/12 lg:mx-auto mt-8'
              )}
            >
              {!!review.entertainmentImage.length && (
                <BCCarousel showThumbs={false}>
                  {review.entertainmentImage.map((item, index) => (
                    <div className="relative w-full h-full" key={item.id}>
                      <Image
                        src={
                          process.env.mediaUrl +
                          item.image
                            ?.replace('/', '')
                            .replace('view/image/', '')
                        }
                        blurDataURL={
                          index
                            ? `/_next/image?url=${
                                process.env.mediaUrl +
                                item.image
                                  ?.replace('/', '')
                                  .replace('view/image/', '')
                              }&w=16&q=1`
                            : null
                        }
                        alt={`Airline review Entertainment - ${review.airline.name} - ${index}`}
                        layout="fill"
                        objectFit="contain"
                        placeholder={index ? 'blur' : null}
                      />
                    </div>
                  ))}
                </BCCarousel>
              )}
            </div>
          </div>
          <div
            ref={amenitiesRef}
            className={cn(
              'container grid grid-cols-12 my-20 sm:px-5',
              styles.scrollContainer
            )}
          >
            <div className="col-span-9 sm:col-span-12">
              <div className="w-full grid grid-cols-12 gap-x-2">
                <div
                  className={cn(
                    styles.header,
                    'font- col-span-12 lg:col-span-4'
                  )}
                >
                  <h3 className={styles.cap}>Amenities &amp; Facilities</h3>
                  {!!review.amenititesRating && (
                    <div className={styles.score}>
                      <span className={styles.points}>
                        {review.amenititesRating}
                      </span>
                      <span>/10</span>
                    </div>
                  )}
                </div>
                <div
                  className={cn(
                    styles.sectionDescription,
                    'col-span-12 lg:col-span-8 font-libre'
                  )}
                >
                  <div className={cn(styles.article)}>
                    {replaceContent(review.amenitites)}
                  </div>
                </div>
              </div>
            </div>
            <div
              className={cn(
                styles.carouselWrapper,
                'col-span-12 lg:w-11/12 lg:mx-auto mt-8'
              )}
            >
              {!!review.amenititesImage.length && (
                <BCCarousel showThumbs={false}>
                  {review.amenititesImage.map((item, index) => (
                    <div className="relative w-full h-full" key={item.id}>
                      <Image
                        src={
                          process.env.mediaUrl +
                          item.image
                            ?.replace('/', '')
                            .replace('view/image/', '')
                        }
                        blurDataURL={
                          index
                            ? `/_next/image?url=${
                                process.env.mediaUrl +
                                item.image
                                  ?.replace('/', '')
                                  .replace('view/image/', '')
                              }&w=16&q=1`
                            : null
                        }
                        alt={`Airline review Amenities & Facilities - ${review.airline.name} - ${index}`}
                        layout="fill"
                        objectFit="contain"
                        placeholder={index ? 'blur' : null}
                      />
                    </div>
                  ))}
                </BCCarousel>
              )}
            </div>
          </div>
          <div
            ref={mediumHaulRef}
            className={cn(
              'container grid grid-cols-12 my-20 sm:px-5',
              styles.scrollContainer
            )}
          >
            <div className="col-span-9 sm:col-span-12">
              <div className="w-full grid grid-cols-12 gap-x-2">
                <div
                  className={cn(
                    styles.header,
                    'font- col-span-12 lg:col-span-4'
                  )}
                >
                  <h3 className={styles.cap}>Short &amp; Medium Haul</h3>
                  {!!review.mediumHaulRating && (
                    <div className={styles.score}>
                      <span className={styles.points}>
                        {review.mediumHaulRating}
                      </span>
                      <span>/10</span>
                    </div>
                  )}
                </div>
                <div
                  className={cn(
                    styles.sectionDescription,
                    'col-span-12 lg:col-span-8 font-libre'
                  )}
                >
                  <div className={cn(styles.article)}>
                    {replaceContent(review.mediumHaul)}
                  </div>
                </div>
              </div>
            </div>
            <div
              className={cn(
                styles.carouselWrapper,
                'col-span-12 lg:w-11/12 lg:mx-auto mt-8'
              )}
            >
              {!!review.mediumHaulImage.length && (
                <BCCarousel showThumbs={false}>
                  {review.mediumHaulImage.map((item, index) => (
                    <div className="relative w-full h-full" key={item.id}>
                      <Image
                        src={
                          process.env.mediaUrl +
                          item.image
                            ?.replace('/', '')
                            .replace('view/image/', '')
                        }
                        blurDataURL={
                          index
                            ? `/_next/image?url=${
                                process.env.mediaUrl +
                                item.image
                                  ?.replace('/', '')
                                  .replace('view/image/', '')
                              }&w=16&q=1`
                            : null
                        }
                        alt={`Airline review Short- & Medium-haul - ${review.airline.name} - ${index}`}
                        layout="fill"
                        objectFit="contain"
                        placeholder={index ? 'blur' : null}
                      />
                    </div>
                  ))}
                </BCCarousel>
              )}
            </div>
          </div>
          <div
            ref={sustainabilityRef}
            className={cn(
              'container grid grid-cols-12 my-20 sm:px-5',
              styles.scrollContainer
            )}
          >
            <div className="col-span-9 sm:col-span-12">
              <div className="w-full grid grid-cols-12 gap-x-2">
                <div
                  className={cn(
                    styles.header,
                    'font- col-span-12 lg:col-span-4'
                  )}
                >
                  <h3 className={styles.cap}>Sustainability</h3>
                </div>
                <div
                  className={cn(
                    styles.sectionDescription,
                    'col-span-12 lg:col-span-8 font-libre'
                  )}
                >
                  <div className={cn(styles.article)}>
                    {replaceContent(review.sustainability)}
                  </div>
                </div>
              </div>
            </div>
            <div
              className={cn(
                styles.carouselWrapper,
                'col-span-12 lg:w-11/12 lg:mx-auto mt-8'
              )}
            >
              {!!review.sustainabilityImage.length && (
                <BCCarousel showThumbs={false}>
                  {review.sustainabilityImage.map((item, index) => (
                    <div className="relative w-full h-full" key={item.id}>
                      <Image
                        src={
                          process.env.mediaUrl +
                          item.image
                            ?.replace('/', '')
                            .replace('view/image/', '')
                        }
                        blurDataURL={
                          index
                            ? `/_next/image?url=${
                                process.env.mediaUrl +
                                item.image
                                  ?.replace('/', '')
                                  .replace('view/image/', '')
                              }&w=16&q=1`
                            : null
                        }
                        alt={`Airline review Sustainability - ${review.airline.name} - ${index}`}
                        layout="fill"
                        objectFit="contain"
                        placeholder={index ? 'blur' : null}
                      />
                    </div>
                  ))}
                </BCCarousel>
              )}
            </div>
          </div>
          <div
            ref={weLoveRef}
            className={cn(
              'container grid grid-cols-12 mt-20 mb-8 sm:px-5',
              styles.scrollContainer
            )}
          >
            <div className="col-span-9 sm:col-span-12">
              <div className="w-full grid grid-cols-12 gap-x-2">
                <div
                  className={cn(
                    styles.header,
                    'font- col-span-12 lg:col-span-4'
                  )}
                >
                  <h3 className={styles.cap}>What We Love</h3>
                </div>
                <div
                  className={cn(
                    styles.sectionDescription,
                    'col-span-12 lg:col-span-8 font-libre'
                  )}
                >
                  <div className={cn(styles.weLove, styles.article)}>
                    {replaceContent(review.weLove)}
                  </div>
                </div>
              </div>
            </div>

            {includesSponsoredLinks && (
              <div
                className={cn(
                  styles.article,
                  'mt-2 font-libre col-span-12 lg:col-span-8 lg:col-start-3 bg-white p-3 mx-auto',
              )}>
                <p>If we don`t fly, stay or visit - we won`t review it. If you Search & Buy anything through our links, we may receive a commission - which keeps us flying, staying and visiting! <a href="/about#rating">Learn more</a></p>
             </div>
            )}
           
            <div
              className={cn(
                'mt-12 font-fragment col-span-12',
                styles.weLove,
                styles.article
              )}
            >
              <p>
                <a href="/">First-Class.com</a> searches hundreds of travel
                sites at once to help you find the best premium travel offers
                for both flights and the finest hotels.
                {/* <span className={cn(styles.weLoveSubline, 'font-libre' )}>
                    Find the best price for{' '}
                  {`${review.airline.name}
          ${
                    CABIN_CLASSES.find((cabin) => cabin.value === review.cabinClass)
                      .label
                  }`}{' '}
                  by making a search on below.
                </span> */}
              </p>
            </div>
          </div>
          {!!review.questionsAnswers?.length && (
            <div className="container flex flex-col justify-center items-center sm:w-full mb-8">
              <span
                className={cn(styles.listTitle, 'text-3xl font-fragment mb-4')}
              >
                FAQs
              </span>
              {review.questionsAnswers.map((faq) => (
                <ScrollableContent
                  key={faq.id}
                  heading={faq.question}
                  content={faq.answer}
                  headingClass={cn(styles.scrollableHeading, 'font-libre')}
                  style={styles.scrollableContent}
                />
              ))}
            </div>
          )}
          {review.userBio.content && (
            <div className="container px-4">
              <AuthorBlock userBio={review.userBio} />
            </div>
          )}
          <div className="container flex flex-col mt-8 sm:hidden">
            <div className="mb-4">
              <div
                className={cn({
                  [styles.flightSearchHorizontal]: true,
                })}
              >
                {renderSearch(() => {}, 'mainSearch', true)}
              </div>
            </div>
          </div>
          <InView triggerOnce>
            {({ inView, ref }) => (
              <div ref={ref} className="container sm:mx-4 mx-auto mb-20">
                {inView && (
                  <div>
                    <SocialMediaLinks />
                  </div>
                )}
              </div>
            )}
          </InView>

          <div className="container flex justify-center items-center mb-20 w-48 mx-auto">
            <VintageButton
              href="/airlines/reviews/"
              className={cn(styles.moreReviewsButton, 'relative')}
              label={'More airline reviews'}
              dark={true}
            ></VintageButton>
          </div>

          {userStore && !userStore.userid && (
            <InView triggerOnce>
              {({ inView, ref }) => (
                <div ref={ref} className="w-full mb-20 sm:mb-10">
                  {inView && (
                    <div>
                      <CallToSignUp
                        country={userStore?.country}
                        authCompleted={authCompleted}
                        sourcePage={'Airline Review'}
                      />
                    </div>
                  )}
                </div>
              )}
            </InView>
          )}

          <div>
            <InView triggerOnce>
              {({ inView, ref }) => (
                <div ref={ref} className="container">
                  {inView && <RecommendationsList items={recommendation} />}
                </div>
              )}
            </InView>
          </div>
        </div>
      </ThemeProvider>
    </>
  );
}

export default withLayout(AirlineReview, true);
